import { BeneficiaryAggregateDateDimension } from '@repo/shared/dtos/beneficiary'
import { Button } from '@repo/ui/button'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Plus, Trash2 } from 'lucide-react'
import { Fragment, useMemo } from 'react'

import { formatDate, transformDate } from './helpers'

export default function SimpleDateBuckets({
  dimension,
  onChange,
}: {
  dimension: BeneficiaryAggregateDateDimension
  onChange: (dimension: BeneficiaryAggregateDateDimension) => void
}) {
  const { t } = useT({ keyPrefix: 'admin.insights.form.dimensions.buckets.date' })
  const buckets = useMemo(() => dimension.buckets, [dimension])

  const updateProperty = (
    index: number,
    property: 'label' | 'min' | 'max',
    value: string | number,
  ) => {
    onChange({
      ...dimension,
      buckets: buckets.map((b, i) => (i === index ? { ...b, [property]: value } : b)),
    })
  }

  const addBucket = () => {
    onChange({
      ...dimension,
      buckets: [...buckets, { label: '', min: undefined, max: undefined }],
    })
  }

  const deleteBucket = (index: number) => {
    onChange({
      ...dimension,
      buckets: buckets.filter((_, i) => i !== index),
    })
  }

  return (
    <div className="space-y-2">
      <div className="grid [grid-template-columns:2fr_1fr_3rem] gap-2">
        <div className="text-sm font-medium text-primary-600">{t('columns.label')}</div>
        <div className="text-sm font-medium text-primary-600">{t('columns.min')}</div>
        <div></div>
        {buckets.map((bucket, index) => (
          <Fragment key={index}>
            <Input
              id={`label-${index}`}
              type="text"
              placeholder={t('labelPlaceholder')}
              value={bucket.label}
              onChange={(event) => updateProperty(index, 'label', event.target.value)}
            />
            <Input
              id={`min-${index}`}
              type="date"
              placeholder={t('minPlaceholder')}
              value={formatDate(bucket.min)}
              onChange={(event) => updateProperty(index, 'min', transformDate(event.target.value))}
            />
            <Button variant="outline" size="sm" onClick={() => deleteBucket(index)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </Fragment>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={() => addBucket()} className="w-full">
        <Plus className="w-4 h-4" />
        {t('addBucket')}
      </Button>
    </div>
  )
}
